import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { registerDonor } from '../hooks/useSupabase';
import { validateDonor, BLOOD_GROUPS, DISTRICTS_KERALA } from '../types/schema';
import toast from 'react-hot-toast';

/* ─── Step indicator ─────────────────────────────────────── */
const steps = [
  { n: 1, label: 'Personal' },
  { n: 2, label: 'Medical' },
];

const StepIndicator = ({ current }) => (
  <div className="flex items-center justify-center mb-10">
    {steps.map((s, i) => (
      <React.Fragment key={s.n}>
        <div className="flex flex-col items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
            s.n < current  ? 'bg-crimson-600 text-white shadow-glow-sm'
            : s.n === current ? 'bg-crimson-600 text-white shadow-glow ring-4 ring-crimson-100'
            : 'bg-surface-100 text-surface-400'
          }`}>
            {s.n < current ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            ) : s.n}
          </div>
          <span className={`text-xs mt-2 font-medium transition-colors ${
            s.n <= current ? 'text-crimson-600' : 'text-surface-400'
          }`}>{s.label}</span>
        </div>
        {i < steps.length - 1 && (
          <div className={`flex-1 h-0.5 mx-3 mb-6 transition-all duration-500 ${
            s.n < current ? 'bg-crimson-600' : 'bg-surface-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

/* ─── Main Component ──────────────────────────────────────── */
const DonorRegistration = ({ onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);

  // Read QR params
  const isQRFlow = searchParams.get('ref') === 'qr';
  const qrLabel = searchParams.get('label') || '';
  const qrDistrict = searchParams.get('district') || '';
  const qrBloodGroup = searchParams.get('bloodGroup') || '';
  const qrExpiry = searchParams.get('exp') || '';
  const qrSource = searchParams.get('source') || searchParams.get('event') || '';

  const isExpired = qrExpiry ? new Date(qrExpiry) < new Date() : false;

  const [formData, setFormData] = useState({
    name: '', email: '', phoneNumber: '', bloodGroup: qrBloodGroup,
    location: '', district: qrDistrict, parish: '',
    age: '', weight: '', lastDonationDate: '',
    isAvailable: true,
    privacySettings: { showPhoneNumber: true, showEmail: true, showLocation: true },
    notificationPreferences: {
      bloodRequests: true, urgentRequests: true, compatibleBloodGroups: true,
      locationRadius: 50, enabledMethods: ['push'],
      quietHours: { enabled: false, start: '22:00', end: '07:00' }
    },
    showEmail: true, showFullName: true,
    points: 0, totalDonations: 0, achievements: [],
  });

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [registered, setRegistered] = useState(null); // holds { name, email, phoneNumber } on success

  // Scroll to top whenever step changes so user starts at the top of each page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Scroll to top when registration succeeds and success screen is shown
  useEffect(() => {
    if (registered) window.scrollTo(0, 0);
  }, [registered]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!formData.name.trim()) return 'Name is required';
      if (!formData.email) return 'Email is required';
      if (!formData.phoneNumber || !/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) return 'Enter a valid 10-digit phone number';
      if (!formData.bloodGroup) return 'Select your blood group';
      if (!formData.district) return 'Select your district';
      if (!formData.location.trim()) return 'Enter your current location';
    }
    if (step === 2) {
      if (!formData.age || parseInt(formData.age) < 18 || parseInt(formData.age) > 65) return 'Age must be between 18 and 65';
      if (!formData.weight || parseFloat(formData.weight) < 45) return 'Weight must be at least 45kg';
    }
    return null;
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep(s => s + 1);
  };

  const checkDuplicateDonor = async (email, phoneNumber) => {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/donors`;
    // Check: same email AND same phone on the same record
    const checkUrl = `${url}?email=eq.${encodeURIComponent(email)}&phone_number=eq.${encodeURIComponent(cleanPhone)}&select=id,email,phone_number&limit=1`;

    const response = await fetch(checkUrl, {
      headers: {
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      }
    });

    if (!response.ok) return null; // Allow registration if check fails

    const data = await response.json();
    return data && data.length > 0 ? data[0] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!consentGiven) {
      toast.error('Please read and accept the consent declaration to continue.');
      return;
    }

    const validationErrors = validateDonor({ ...formData, age: parseInt(formData.age), weight: parseFloat(formData.weight) });
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the validation errors');
      return;
    }

    setLoading(true);
    setErrors([]);

    // Check for duplicate donor (same email AND phone)
    const existingDonor = await checkDuplicateDonor(formData.email, formData.phoneNumber);
    if (existingDonor) {
      setLoading(false);
      setDuplicateError(true);
      setErrors(['A donor profile already exists with this exact email and phone number combination. You may already be registered — please login instead.']);
      toast.error('Duplicate registration: this email + phone number is already registered.');
      setStep(1); // Go back to step 1 so user can see the fields
      return;
    }
    setDuplicateError(false);

    try {
      const donorData = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        bloodGroup: formData.bloodGroup,
        location: formData.location,
        district: formData.district,
        parish: formData.parish || '',
        age: parseInt(formData.age) || 0,
        weight: parseFloat(formData.weight) || 0,
        lastDonationDate: formData.lastDonationDate ? new Date(formData.lastDonationDate) : null,
        isAvailable: true,
        ...(isQRFlow && { registrationSource: 'qr', qrLabel, qrSource }),
        privacySettings: { showPhoneNumber: true, showEmail: formData.showEmail, showLocation: true },
        notificationPreferences: {
          bloodRequests: true, urgentRequests: true, compatibleBloodGroups: true, locationRadius: 50,
          enabledMethods: ['push'], quietHours: { enabled: false, start: '22:00', end: '07:00' }
        },
        points: 0, totalDonations: formData.lastDonationDate ? 1 : 0, achievements: [],
        authUid: null,
      };
      const result = await registerDonor(donorData);
      if (onSuccess) onSuccess(result);
      setRegistered({
        name: donorData.name,
        email: donorData.email,
        phoneNumber: donorData.phoneNumber,
        profileUrl: `/donor-profile?id=${result.id}&email=${encodeURIComponent(donorData.email)}&phone=${encodeURIComponent(donorData.phoneNumber)}`,
      });
    } catch (error) {
      console.error('[DonorRegistration] Registration error:', error.message);
      toast.error(error.message || 'Registration failed. Please try again.');
      setErrors([error.message || 'Unknown error']);
    } finally {
      setLoading(false);
    }
  };

  /* ── Success Screen ── */
  if (registered) {
    const confettiColors = ['#f9efd5', '#e8c47a', '#ffffff', '#d4a96a', '#ff6b6b', '#4ecdc4'];
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-16 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #5c3a1e 0%, #8f2a0e 40%, #c4451a 75%, #d4a96a 100%)' }}>

        {/* Confetti particles */}
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 18 }).map((_, i) => (
            <div key={i} className="absolute animate-confetti"
              style={{
                left: `${5 + (i * 5.5) % 90}%`,
                width: `${4 + (i % 3) * 3}px`,
                height: `${6 + (i % 4) * 3}px`,
                background: confettiColors[i % confettiColors.length],
                borderRadius: i % 2 === 0 ? '2px' : '50%',
                animationDelay: `${(i * 80) % 1200}ms`,
                animationDuration: `${2 + (i % 3) * 0.6}s`,
              }}
            />
          ))}
        </div>

        <div className="w-full max-w-md relative z-10">

          {/* Checkmark circle with pop-in */}
          <div className="flex justify-center mb-6 animate-popIn">
            <div className="w-24 h-24 rounded-full flex items-center justify-center shadow-2xl animate-pulse-crimson"
              style={{ background: 'linear-gradient(135deg, #f9efd5, #e8c47a)', border: '4px solid rgba(255,255,255,0.4)' }}>
              <svg className="w-12 h-12" fill="none" stroke="#5c3a1e" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path className="animate-strokeDraw" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Heading with stagger */}
          <h1 className="text-3xl font-black text-white text-center mb-2 animate-fadeInUp delay-200"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
            You're Registered! 🎉
          </h1>
          <p className="text-center mb-8 animate-fadeInUp delay-300" style={{ color: 'rgba(255,255,255,0.8)' }}>
            Welcome to the OCYM Blood Donors Network, <strong className="text-white">{registered.name.split(' ')[0]}</strong>!
          </p>

          {/* Login hint card with stagger */}
          <div className="rounded-2xl p-6 mb-5 animate-fadeInUp delay-400" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.25)' }}>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">🔑</span>
              <h2 className="font-bold text-white text-base">How to Login Next Time</h2>
            </div>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.85)' }}>
              You can sign in anytime using your registered details:
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 rounded-xl px-4 py-3 animate-fadeInUp delay-500" style={{ background: 'rgba(0,0,0,0.2)' }}>
                <span className="text-lg shrink-0">📧</span>
                <div className="min-w-0">
                  <p className="text-xs font-medium mb-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Email Address</p>
                  <p className="text-sm font-bold text-white truncate">{registered.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 rounded-xl px-4 py-3 animate-fadeInUp delay-500" style={{ background: 'rgba(0,0,0,0.2)' }}>
                <span className="text-lg shrink-0">📱</span>
                <div>
                  <p className="text-xs font-medium mb-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Mobile Number</p>
                  <p className="text-sm font-bold text-white">{registered.phoneNumber}</p>
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-xl px-4 py-3 flex items-start gap-2 animate-fadeInUp delay-500"
              style={{ background: 'rgba(212,169,106,0.25)', border: '1px solid rgba(212,169,106,0.4)' }}>
              <span className="text-sm shrink-0 mt-0.5">💡</span>
              <p className="text-xs" style={{ color: '#f9efd5' }}>
                Save these details. You'll need your <strong>email</strong> and <strong>phone number</strong> to log in on any device.
              </p>
            </div>
          </div>

          {/* Action buttons with stagger */}
          <div className="space-y-3 animate-fadeInUp delay-600">
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3.5 rounded-2xl font-bold text-base transition-all hover:-translate-y-0.5 hover:shadow-xl"
              style={{ background: 'white', color: '#8f2a0e', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}
            >
              🔐 Login Now
            </button>
            <button
              onClick={() => navigate(registered.profileUrl)}
              className="w-full py-3.5 rounded-2xl font-semibold text-sm transition-all hover:-translate-y-0.5"
              style={{ background: 'rgba(255,255,255,0.15)', border: '2px solid rgba(255,255,255,0.35)', color: 'white' }}
            >
              View My Profile →
            </button>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header band */}
      <div className="bg-hero py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-pattern" />
        <div className="relative section-container text-center">
          {isQRFlow ? (
            <div className="hero-pill mb-4 inline-flex">📲 Quick QR Registration</div>
          ) : (
            <div className="hero-pill mb-4 inline-flex">❤️ Join the Network</div>
          )}
          <h1 className="text-4xl font-black text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em' }}>
            Register as Blood Donor
          </h1>
          <p className="text-crimson-200 mt-2">
            {isQRFlow && qrLabel ? `${qrLabel}` : 'Share your details to get matched with people in urgent need'}
          </p>
        </div>
      </div>

      {/* QR info banner */}
      {isQRFlow && (
        <div className={`max-w-2xl mx-auto mt-4 mx-4 px-4 ${
          isExpired
            ? 'bg-red-50 border border-red-200 text-red-800'
            : 'bg-indigo-50 border border-indigo-200 text-indigo-800'
        } rounded-xl p-4 flex items-start gap-3`}>
          <span className="text-xl">{isExpired ? '⚠️' : '✅'}</span>
          <div>
            {isExpired ? (
              <>
                <p className="font-bold text-sm">This QR code has expired</p>
                <p className="text-xs mt-0.5 opacity-80">Please ask the organiser for a new QR code. You can still register manually below.</p>
              </>
            ) : (
              <>
                <p className="font-bold text-sm">Quick Registration via QR Code</p>
                <p className="text-xs mt-0.5 opacity-80">
                  {qrLabel && <span>Event: <strong>{qrLabel}</strong> · </span>}
                  {qrDistrict && <span>District pre-filled: <strong>{qrDistrict}</strong> · </span>}
                  {qrBloodGroup && <span>Blood group pre-filled: <strong>{qrBloodGroup}</strong></span>}
                  {!qrLabel && !qrDistrict && !qrBloodGroup && 'Fill in your details to register quickly.'}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <div className="section-container py-12">
        <div className="max-w-xl mx-auto">

          <StepIndicator current={step} />

          {/* Duplicate donor error banner */}
          {duplicateError && (
            <div className="mb-6 p-4 rounded-xl border-2 animate-scaleIn flex items-start gap-3"
              style={{ background: '#fef2f2', borderColor: '#fca5a5' }}>
              <span className="text-2xl shrink-0">🚫</span>
              <div>
                <p className="font-bold text-sm text-red-800">Already Registered</p>
                <p className="text-sm text-red-700 mt-1">
                  A donor with this <strong>email address</strong> and <strong>phone number</strong> combination is already registered.
                </p>
                <p className="text-xs text-red-600 mt-2">
                  If this is you, please <a href="/login" className="underline font-semibold">sign in</a> instead. If you think this is an error, contact support.
                </p>
              </div>
            </div>
          )}

          {/* General error list */}
          {errors.length > 0 && !duplicateError && (
            <div className="mb-6 p-4 rounded-xl bg-crimson-50 border border-crimson-200 animate-scaleIn">
              <h4 className="text-crimson-800 font-semibold text-sm mb-2">Please fix the following:</h4>
              <ul className="space-y-1">
                {errors.map((err, i) => (
                  <li key={i} className="text-crimson-700 text-sm flex items-start gap-2">
                    <span className="mt-0.5">⚠️</span> {err}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Step 1: Personal ── */}
          {step === 1 && (
            <div className="card-elevated p-8 animate-fadeInUp">
              <h2 className="text-xl font-bold text-surface-900 mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Personal Information</h2>
              <p className="text-sm text-surface-500 mb-6">Help us match you with nearby blood requests</p>
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Full Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange}
                      required className="input-field" placeholder="Enter your full name" />
                  </div>
                  <div>
                    <label className="field-label">Email Address *</label>
                    <input type="email" name="email" value={formData.email}
                      onChange={(e) => { setDuplicateError(null); handleChange(e); }}
                      required
                      className={`input-field ${duplicateError ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-200' : ''}`}
                      placeholder="you@example.com" />
                    {duplicateError && <p className="mt-1 text-xs text-red-600 font-medium">⚠️ Already registered</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Phone Number *</label>
                    <input type="tel" name="phoneNumber" value={formData.phoneNumber}
                      onChange={(e) => { setDuplicateError(null); handleChange(e); }}
                      required
                      className={`input-field ${duplicateError ? 'border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-200' : ''}`}
                      placeholder="10-digit number" />
                    {duplicateError && <p className="mt-1 text-xs text-red-600 font-medium">⚠️ Already registered</p>}
                  </div>
                  <div>
                    <label className="field-label">Blood Group *</label>
                    <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange} required className="input-field">
                      <option value="">Select</option>
                      {BLOOD_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label">Current Residing District *</label>
                    <select name="district" value={formData.district} onChange={handleChange} required className="input-field">
                      <option value="">Select your district</option>
                      {DISTRICTS_KERALA.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Location/Town *</label>
                    <input type="text" name="location" value={formData.location} onChange={handleChange}
                      required className="input-field" placeholder="City/Town/Area" />
                  </div>
                </div>
                <div>
                  <label className="field-label">Parish (Optional)</label>
                  <input type="text" name="parish" value={formData.parish} onChange={handleChange}
                    className="input-field" placeholder="Your parish name" />
                </div>
              </div>
              <div className="flex gap-3 mt-8">
                {onCancel && (
                  <button type="button" onClick={onCancel} className="btn btn-ghost btn-md flex-1 border border-surface-200">Cancel</button>
                )}
                <button type="button" onClick={handleNext} className="btn btn-primary btn-md flex-1">
                  Continue
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Medical ── */}
          {step === 2 && (
            <form onSubmit={handleSubmit}>
              <div className="card-elevated p-8 animate-fadeInUp">
                <h2 className="text-xl font-bold text-surface-900 mb-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Medical Details</h2>
                <p className="text-sm text-surface-500 mb-6">Required to determine your donation eligibility</p>
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="field-label">Age *</label>
                      <input type="number" name="age" value={formData.age} onChange={handleChange}
                        required min="18" max="65" className="input-field" placeholder="18–65 years" />
                    </div>
                    <div>
                      <label className="field-label">Weight (kg) *</label>
                      <input type="number" name="weight" value={formData.weight} onChange={handleChange}
                        required min="45" step="0.1" className="input-field" placeholder="Min. 45 kg" />
                    </div>
                  </div>
                  <div>
                    <label className="field-label">Last Donation Date <span className="text-surface-400 font-normal">(Optional)</span></label>
                    <input type="date" name="lastDonationDate" value={formData.lastDonationDate} onChange={handleChange}
                      max={new Date().toISOString().split('T')[0]} className="input-field" />
                    <p className="helper-text">Leave blank if this is your first donation</p>
                  </div>

                  {/* Privacy */}
                  <div className="p-5 rounded-xl bg-surface-50 border border-surface-100 space-y-4">
                    <h3 className="text-sm font-semibold text-surface-700">Privacy Settings</h3>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`relative w-10 h-6 rounded-full transition-colors ${formData.showEmail ? 'bg-crimson-500' : 'bg-surface-300'}`}>
                        <input type="checkbox" name="showEmail" checked={formData.showEmail} onChange={handleChange} className="sr-only" />
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${formData.showEmail ? 'left-5' : 'left-1'}`} />
                      </div>
                      <span className="text-sm text-surface-700 group-hover:text-surface-900">Show my email to OCYM admins &amp; coordinators</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`relative w-10 h-6 rounded-full transition-colors ${formData.showFullName ? 'bg-crimson-500' : 'bg-surface-300'}`}>
                        <input type="checkbox" name="showFullName" checked={formData.showFullName} onChange={handleChange} className="sr-only" />
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${formData.showFullName ? 'left-5' : 'left-1'}`} />
                      </div>
                      <span className="text-sm text-surface-700 group-hover:text-surface-900">Show my full name (recommended)</span>
                    </label>
                  </div>

                  {/* Eligibility info */}
                  <div className="p-5 rounded-xl bg-sky-50 border border-sky-100">
                    <h4 className="text-sm font-semibold text-sky-800 mb-3 flex items-center gap-2">
                      <span>ℹ️</span> Eligibility Requirements
                    </h4>
                    <ul className="text-sky-700 text-xs space-y-2">
                      {[
                        'Age between 18–65 years and weight at least 45kg',
                        'Minimum 8-week gap between donations',
                        'Your info will be used to match you with blood requests',
                        'You can update availability and donation history anytime',
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-sky-400 mt-0.5 shrink-0">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                  {/* Consent */}
                  <div className={`rounded-xl border-2 transition-all duration-300 overflow-hidden ${consentGiven ? 'border-green-300 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                    {/* Collapsed state — single line after consent given */}
                    {consentGiven ? (
                      <label className="flex items-center gap-3 px-4 py-3 cursor-pointer">
                        <input type="checkbox" checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} className="sr-only" />
                        <div className="w-5 h-5 rounded flex items-center justify-center border-2 bg-green-500 border-green-500 shrink-0">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <span className="text-sm font-semibold text-green-800">✅ Consent given — I voluntarily register as a blood donor with OCYM Raktadhan.</span>
                      </label>
                    ) : (
                      /* Expanded state — full consent text */
                      <label className="flex items-start gap-3 p-5 cursor-pointer">
                        <input type="checkbox" checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} className="sr-only" />
                        <div className="mt-0.5 w-5 h-5 rounded flex items-center justify-center border-2 bg-white border-amber-400 shrink-0">
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-amber-800 mb-2">⚠️ Consent Required — please read and tick to continue</p>
                          <p className="text-xs leading-relaxed text-surface-700 mb-3">
                            I voluntarily register as a blood donor with <strong>OCYM Raktadhan</strong> and confirm that:
                          </p>
                          <ol className="space-y-2 text-xs text-surface-700 list-decimal list-outside pl-4">
                            <li className="leading-relaxed">I consent to being contacted by OCYM coordinators or blood requesters when my blood group is urgently needed.</li>
                            <li className="leading-relaxed">I confirm that I am between 18 and 65 years of age and weigh at least 45 kg.</li>
                            <li className="leading-relaxed">I am in good general health and am not currently suffering from any condition that would prevent me from donating blood.</li>
                            <li className="leading-relaxed">I understand that blood donation is voluntary, and I may decline any donation request at my discretion.</li>
                            <li className="leading-relaxed">I understand that my personal details will be used only for blood donation coordination within the OCYM network and will not be shared for commercial purposes.</li>
                          </ol>
                          <p className="mt-3 text-xs font-medium text-amber-700">← Tick the checkbox to confirm your consent</p>
                        </div>
                      </label>
                    )}
                  </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button type="button" onClick={() => setStep(1)} className="btn btn-ghost btn-md border border-surface-200 shrink-0 px-5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                    </svg>
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !consentGiven}
                    className={`btn flex-1 transition-all duration-300 ${
                      consentGiven
                        ? 'btn-primary btn-lg shadow-lg scale-100'
                        : 'btn-md bg-surface-200 text-surface-400 cursor-not-allowed'
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Registering…
                      </span>
                    ) : consentGiven ? (
                      <>❤️ Complete Registration</>
                    ) : (
                      <>Complete Registration</>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonorRegistration;
